import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

const GAP_THRESHOLD_PX = 12;
const GAP_CHECK_DELAYS_MS = [64, 240, 520, 1200];

export interface SafeAreaFallbackOptions {
  enableLogging?: boolean;
}

const LOG_PREFIX = '[SmartDocs][SafeAreaFallback]';

export const useSafeAreaFallback = (options: SafeAreaFallbackOptions = {}) => {
  const { enableLogging = true } = options;
  const [resumeTick, setResumeTick] = useState(0);
  const gapTimers = useRef<number[]>([]);

  const log = useCallback(
    (stage: string, detail?: Record<string, unknown>) => {
      if (enableLogging) {
        console.log(`${LOG_PREFIX} ${stage}`, detail ?? {});
      }
    },
    [enableLogging]
  );

  const applySafeAreaFix = useCallback(
    (stage?: string) => {
      const ionApp = document.querySelector('ion-app');

      if (!ionApp) {
        log('ion-app-missing', { stage });
        return;
      }

      log('apply-safe-area::before', { stage, paddingTop: ionApp.style.paddingTop });

      ionApp.style.removeProperty('--ion-safe-area-top');
      ionApp.style.removeProperty('--ion-safe-area-bottom');

      requestAnimationFrame(() => {
        ionApp.style.setProperty('--ion-safe-area-top', 'env(safe-area-inset-top)');
        ionApp.style.setProperty('--ion-safe-area-bottom', 'env(safe-area-inset-bottom)');
        window.dispatchEvent(new Event('resize'));

        log('apply-safe-area::after', {
          stage,
          paddingTop: getComputedStyle(ionApp).paddingTop,
        });
      });
    },
    [log]
  );

  const forceLightweightRedraw = useCallback(
    (stage?: string) => {
      const ionContentElements = Array.from(document.querySelectorAll('ion-content')) as HTMLElement[];

      ionContentElements.forEach((element, index) => {
        const existingToken = element.style.getPropertyValue('--smartdocs-resume-tick');
        const nextToken = existingToken === '1' ? '2' : '1';
        element.style.setProperty('--smartdocs-resume-tick', nextToken || '1');
        element.style.setProperty('--smartdocs-redraw-index', String(index));
        void element.offsetHeight;
      });

      log('force-redraw', { stage, count: ionContentElements.length });
      setResumeTick((value) => value + 1);
    },
    [log]
  );

  const detectGap = useCallback(() => {
    const header = document.querySelector('ion-header');
    if (!header) {
      return false;
    }

    const rect = header.getBoundingClientRect();
    return rect.top > GAP_THRESHOLD_PX;
  }, []);

  const clearGapTimers = useCallback(() => {
    gapTimers.current.forEach((timerId) => window.clearTimeout(timerId));
    gapTimers.current = [];
  }, []);

  const scheduleGapChecks = useCallback(
    (baseStage: string) => {
      clearGapTimers();

      GAP_CHECK_DELAYS_MS.forEach((delay, index) => {
        const attempt = index + 1;
        const timerId = window.setTimeout(() => {
          gapTimers.current = gapTimers.current.filter((id) => id !== timerId);

          const stage = `${baseStage}::attempt-${attempt}`;
          const gapDetected = detectGap();

          log('gap-check', { stage, gapDetected });

          if (!gapDetected) {
            return;
          }

          applySafeAreaFix(stage);
          const stillGap = detectGap();
          log('gap-check::post-safe-area', { stage, gapDetected: stillGap });

          if (!stillGap) {
            return;
          }

          forceLightweightRedraw(stage);
          const afterRedraw = detectGap();

          log('gap-check::post-redraw', { stage, gapDetected: afterRedraw });
        }, delay);

        gapTimers.current.push(timerId);
      });
    },
    [applySafeAreaFix, clearGapTimers, detectGap, forceLightweightRedraw, log]
  );

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleNativeLifecycle = (event: Event) => {
      const customEvent = event as CustomEvent<{ stage?: string }>;
      const { detail = {} } = customEvent;
      const stage = detail.stage ?? 'unknown';

      log('native-event', { stage });

      if (stage === 'willEnterForeground') {
        applySafeAreaFix(stage);
      }

      if (stage === 'didBecomeActive') {
        scheduleGapChecks(stage);
      }
    };

    window.addEventListener('smartdocs:nativeLifecycle', handleNativeLifecycle as EventListener);

    return () => {
      window.removeEventListener('smartdocs:nativeLifecycle', handleNativeLifecycle as EventListener);
    };
  }, [applySafeAreaFix, log, scheduleGapChecks]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleResume = () => {
      requestAnimationFrame(() => {
        forceLightweightRedraw('resume');
      });
    };

    let listener: PluginListenerHandle | undefined;

    if (Capacitor.getPlatform() === 'ios') {
      CapacitorApp.addListener('resume', handleResume).then((handle) => {
        listener = handle;
      });
    }

    return () => {
      listener?.remove();
    };
  }, [forceLightweightRedraw]);

  return {
    resumeTick,
    applySafeAreaFix,
  };
};

